import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MapPin,
  Send,
  Building2,
  Briefcase,
  FileText,
  GraduationCap,
  FlaskConical,
  DollarSign,
  Share2,
  Check
} from "lucide-react";

export default function PositionViewPage() {
  const { positionId } = useParams();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    const fetchPosition = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/public/${positionId}`
        );
        setPosition(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching position:", err);
        if (err.response?.status === 404) {
          setError("Position not found");
        } else {
          setError("Failed to load position. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (positionId) {
      fetchPosition();
    }
  }, [positionId]);

  const formatLocation = () => {
    if (position.city && position.country) {
      return `${position.city}, ${position.country}`;
    }
    return position.city || position.country || "Location not specified";
  };

  const formatStartDate = () => {
    if (!position.start_semester && !position.start_year)
      return "TBD";
    if (position.start_semester && position.start_year)
      return `${position.start_semester} ${position.start_year}`;
    if (position.start_semester) return position.start_semester;
    if (position.start_year) return position.start_year;
    return "TBD";
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  const InfoRow = ({ label, value, dotColor = "blue", isTag = false }) => {
    if (!value && value !== 0) return (
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        padding: "0.75rem 0",
        borderBottom: "1px solid #f3f4f6"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#6b7280"
        }}>
          <div style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: dotColor === "blue" ? "#3b82f6" :
              dotColor === "green" ? "#10b981" :
                dotColor === "purple" ? "#8b5cf6" :
                  dotColor === "orange" ? "#f59e0b" : "#6b7280"
          }} />
          {label}
        </div>
        <div style={{
          fontSize: "0.875rem",
          color: "#9ca3af",
          fontStyle: "italic"
        }}>
          N/A
        </div>
      </div>
    );

    return (
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        padding: "0.75rem 0",
        borderBottom: "1px solid #f3f4f6"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#6b7280"
        }}>
          {dotColor && (
            <div style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: dotColor === "blue" ? "#3b82f6" :
                dotColor === "green" ? "#10b981" :
                  dotColor === "purple" ? "#8b5cf6" :
                    dotColor === "orange" ? "#f59e0b" : "#6b7280"
            }} />
          )}
          {label}
        </div>
        <div style={{
          fontSize: "0.875rem",
          color: "#111827",
          fontWeight: isTag ? 500 : 400,
          backgroundColor: isTag ? "#f3f4f6" : "transparent",
          padding: isTag ? "0.25rem 0.5rem" : "0",
          borderRadius: isTag ? "0.25rem" : "0",
          textAlign: "right",
          maxWidth: "60%"
        }}>
          {value}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f9fafb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #061941",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 1rem",
            }}
          ></div>
          <p style={{ color: "#6b7280", fontSize: "1rem" }}>
            Loading position...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !position) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f9fafb",
          padding: "2rem",
        }}
      >
        <div
          style={{
            textAlign: "center",
            maxWidth: "500px",
            backgroundColor: "white",
            padding: "3rem",
            borderRadius: "1rem",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#111827",
              marginBottom: "0.5rem",
            }}
          >
            Position Not Found
          </h1>
          <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
            {error || "The position you are looking for does not exist."}
          </p>
          <button
            onClick={() => navigate("/student-dashboard")}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#061941",
              color: "white",
              border: "none",
              borderRadius: "0.5rem",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#0a2a5a")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#061941")}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        padding: "2rem 1rem",
      }}
    >
      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
        }}
      >
        {/* Position Header Card */}
        <div
          style={{
            backgroundColor: "#061941",
            color: "white",
            padding: "3rem 2rem",
            borderRadius: "1rem 1rem 0 0",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            <div style={{ flex: 1 }}>
              <h1
                style={{
                  color: "white",
                  fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                  fontWeight: 700,
                  margin: 0,
                  marginBottom: "0.75rem",
                  lineHeight: "1.2",
                }}
              >
                {position.title}
              </h1>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "1.125rem",
                  opacity: 0.95,
                }}
              >
                <MapPin size={20} />
                <span>{formatLocation()}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              alignItems: "flex-end"
            }}>
              {(position.link || position.native) ? (
                <a
                  href={position.link && position.link.startsWith("http") ? position.link : `https://${position.link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.75rem 1.5rem",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "0.5rem",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    transition: "all 0.2s",
                    border: "1px solid rgba(255, 255, 255, 0.2)"
                  }}
                  onClick={(e) => {
                    if (position.native) {
                      e.preventDefault();
                      navigate(`/apply/${position._id}`);
                    }
                    e.stopPropagation();
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                  }}
                >
                  <Send size={18} />
                  Apply Here
                </a>
              ) : null}

              <button
                onClick={handleShare}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: shareCopied ? "rgba(16, 185, 129, 0.2)" : "rgba(139, 191, 194, 0.2)",
                  color: "white",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "0.5rem",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!shareCopied) {
                    e.target.style.backgroundColor = "rgba(139, 191, 194, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!shareCopied) {
                    e.target.style.backgroundColor = "rgba(139, 191, 194, 0.2)";
                  }
                }}
              >
                {shareCopied ? (
                  <>
                    <Check size={18} />
                    Link Copied
                  </>
                ) : (
                  <>
                    <Share2 size={18} />
                    Share
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Position Content Card */}
        <div
          style={{
            backgroundColor: "white",
            padding: "2.5rem",
            borderRadius: "0 0 1rem 1rem",
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Information Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "2rem",
              marginBottom: "2.5rem",
            }}
          >
            {/* Institution Details */}
            <div style={{
              backgroundColor: "#f8fafc",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              border: "1px solid #e2e8f0"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1.5rem"
              }}>
                <div style={{
                  padding: "0.75rem",
                  backgroundColor: "#dbeafe",
                  borderRadius: "0.5rem"
                }}>
                  <Building2 size={24} style={{ color: "#1e40af" }} />
                </div>
                <h3 style={{
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: "#111827",
                  margin: 0
                }}>
                  Institution Details
                </h3>
              </div>

              <InfoRow label="Institution" value={position.institution} dotColor="blue" />
              <InfoRow label="Start Date" value={formatStartDate()} dotColor="blue" />

              {position.fields && position.fields.length > 0 && (
                <div style={{
                  padding: "0.75rem 0",
                  borderBottom: "1px solid #f3f4f6"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#6b7280"
                  }}>
                    <div style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#3b82f6"
                    }} />
                    Research Fields:
                  </div>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem"
                  }}>
                    {position.fields.map((field, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: "0.75rem",
                          color: "#111827",
                          fontWeight: 500,
                          backgroundColor: "#f3f4f6",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "0.25rem"
                        }}
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Financial Details */}
            <div style={{
              backgroundColor: "#f0fdf4",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              border: "1px solid #bbf7d0"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1.5rem"
              }}>
                <div style={{
                  padding: "0.75rem",
                  backgroundColor: "#dcfce7",
                  borderRadius: "0.5rem"
                }}>
                  <DollarSign size={24} style={{ color: "#166534" }} />
                </div>
                <h3 style={{
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: "#111827",
                  margin: 0
                }}>
                  Financial Details
                </h3>
              </div>

              <InfoRow
                label="Funding Status"
                value={position.financial_info?.fully_funded}
                dotColor="green"
              />
              <InfoRow
                label="Funding Source"
                value={position.financial_info?.funding_source}
                dotColor="green"
              />
              <InfoRow
                label="Duration"
                value={position.financial_info?.expected_graduation_time}
                dotColor="green"
              />
            </div>

            {/* Lab Environment (if data exists) */}
            {(position.lab_environment?.size || position.lab_environment?.publication_rate || position.lab_environment?.collaboration) && (
              <div style={{
                backgroundColor: "#faf5ff",
                padding: "1.5rem",
                borderRadius: "0.75rem",
                border: "1px solid #e9d5ff"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem"
                }}>
                  <div style={{
                    padding: "0.75rem",
                    backgroundColor: "#ede9fe",
                    borderRadius: "0.5rem"
                  }}>
                    <FlaskConical size={24} style={{ color: "#7c3aed" }} />
                  </div>
                  <h3 style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    color: "#111827",
                    margin: 0
                  }}>
                    Lab Environment
                  </h3>
                </div>

                <InfoRow label="Lab Size" value={position.lab_environment?.size} dotColor="purple" />
                <InfoRow label="Publication Rate" value={position.lab_environment?.publication_rate} dotColor="purple" />
                <InfoRow label="Collaboration" value={position.lab_environment?.collaboration} dotColor="purple" />
              </div>
            )}

            {/* Requirements (if data exists) */}
            {(position.minimum_requirements?.gpa || position.minimum_requirements?.gre || position.minimum_requirements?.degree_level || position.minimum_requirements?.degree_field || position.minimum_requirements?.toefl || position.minimum_requirements?.research_experience) && (
              <div style={{
                backgroundColor: "#fffbeb",
                padding: "1.5rem",
                borderRadius: "0.75rem",
                border: "1px solid #fed7aa"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem"
                }}>
                  <div style={{
                    padding: "0.75rem",
                    backgroundColor: "#fef3c7",
                    borderRadius: "0.5rem"
                  }}>
                    <GraduationCap size={24} style={{ color: "#d97706" }} />
                  </div>
                  <h3 style={{
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    color: "#111827",
                    margin: 0
                  }}>
                    Requirements
                  </h3>
                </div>

                <InfoRow label="Degree Level" value={position.minimum_requirements?.degree_level} dotColor="orange" />
                <InfoRow label="Degree Field" value={position.minimum_requirements?.degree_field} dotColor="orange" />
                <InfoRow label="GPA" value={position.minimum_requirements?.gpa} dotColor="orange" />
                <InfoRow label="GRE Score" value={position.minimum_requirements?.gre} dotColor="orange" />
                <InfoRow label="TOEFL" value={position.minimum_requirements?.toefl} dotColor="orange" />
                <InfoRow label="Research Experience" value={position.minimum_requirements?.research_experience} dotColor="orange" />
              </div>
            )}
          </div>

          {/* Description Section */}
          <div style={{ marginBottom: "2.5rem" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1.5rem"
            }}>
              <div style={{
                padding: "0.75rem",
                backgroundColor: "#fef3c7",
                borderRadius: "0.5rem"
              }}>
                <FileText size={24} style={{ color: "#d97706" }} />
              </div>
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#111827",
                  margin: 0
                }}
              >
                Role Description
              </h2>
            </div>

            <div
              style={{
                color: "#4b5563",
                lineHeight: "1.75",
                fontSize: "1rem",
                backgroundColor: "#f9fafb",
                padding: "1.5rem",
                borderRadius: "0.5rem",
                border: "1px solid #e5e7eb"
              }}
            >
              {position.description ? position.description.split("\n").map(
                (paragraph, index) =>
                  paragraph.trim() && (
                    <p key={index} style={{ marginBottom: "1.25rem" }}>
                      {paragraph}
                    </p>
                  )
              ) : <p>No description available.</p>}
            </div>
          </div>

          {/* Application Section */}
          {(position.link || position.native) && (
            <div
              style={{
                marginBottom: "2.5rem",
                padding: "1.5rem",
                backgroundColor: "#f0fdf4",
                borderRadius: "0.75rem",
                border: "1px solid #bbf7d0",
              }}
            >
              <h2
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  marginBottom: "1rem",
                  color: "#111827",
                }}
              >
                Application
              </h2>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                {position.link && (
                  <a
                    href={
                      position.link.startsWith("http")
                        ? position.link
                        : `https://${position.link}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.875rem 1.75rem",
                      backgroundColor: "#061941",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "0.5rem",
                      fontWeight: 600,
                      fontSize: "0.9375rem",
                      transition: "all 0.2s",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#0a2a5a";
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow =
                        "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#061941";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    <svg
                      style={{ width: "1.25rem", height: "1.25rem" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    More Information
                  </a>
                )}
                {position.native && (
                  <button
                    onClick={() => {
                      const user = localStorage.getItem("user");
                      if (user) {
                        navigate(`/apply/${position._id}`);
                      } else {
                        navigate("/login", {
                          state: { from: `/apply/${position._id}` },
                        });
                      }
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.875rem 1.75rem",
                      backgroundColor: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "0.5rem",
                      fontWeight: 600,
                      fontSize: "0.9375rem",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#059669";
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow =
                        "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#10b981";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    <svg
                      style={{ width: "1.25rem", height: "1.25rem" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Express Interest
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Back Button */}
          <div
            style={{
              paddingTop: "2rem",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.5rem",
                backgroundColor: "transparent",
                color: "#6b7280",
                border: "1px solid #d1d5db",
                borderRadius: "0.5rem",
                cursor: "pointer",
                fontSize: "0.9375rem",
                fontWeight: 500,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f9fafb";
                e.target.style.borderColor = "#9ca3af";
                e.target.style.color = "#111827";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.borderColor = "#d1d5db";
                e.target.style.color = "#6b7280";
              }}
            >
              <svg
                style={{ width: "1.25rem", height: "1.25rem" }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
          </div>
        </div>
      </div>

      {/* CTA below main card for anonymous users */}
      {!localStorage.getItem("user") && (
        <div
          style={{
            maxWidth: "900px",
            margin: "1.5rem auto 0",
            textAlign: "center",
          }}
        >
          <p
            style={{
              marginBottom: "0.75rem",
              color: "#374151",
              fontSize: "1rem",
            }}
          >
            Want to see more positions like this? Create an account for free.
          </p>
          <button
            onClick={() => navigate("/create-user")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              padding: "0.95rem 1.85rem",
              backgroundColor: "#F2AF29",
              color: "#061941",
              textDecoration: "none",
              borderRadius: "0.6rem",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
              border: "none"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e6a325";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 10px 20px rgba(15, 23, 42, 0.12)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#F2AF29";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 6px 18px rgba(15, 23, 42, 0.08)";
            }}
          >
            Join NeuHawk Today!
          </button>
        </div>
      )}
    </div>
  );
}
